<template>
  <div class="pb-28">
    <!-- Sticky header: identity of the screen, the three modes, and search -->
    <div class="sticky top-0 z-20 bg-gray-100/95 backdrop-blur px-4 pt-4 pb-2 md:px-6 md:pt-6">
      <div class="flex items-center justify-between gap-3 mb-3">
        <h1 class="text-lg md:text-2xl font-bold text-gray-900">לידים ומשימות</h1>
        <div class="flex items-center gap-1.5">
          <Button
            label="ליד חדש"
            icon="pi pi-plus"
            size="small"
            class="!rounded-xl hidden md:inline-flex"
            @click="showNewLeadDialog = true"
          />
          <Button
            icon="pi pi-sync"
            size="small"
            text
            rounded
            :loading="syncing"
            class="!text-gray-400 hover:!text-gray-900"
            title="ייבוא כל העסקים הרשומים כלידים"
            @click="syncLeads"
          />
          <Button
            icon="pi pi-refresh"
            size="small"
            text
            rounded
            :loading="loading"
            class="!text-gray-400 hover:!text-gray-900"
            @click="refresh"
          />
        </div>
      </div>

      <div class="flex md:inline-flex gap-1 bg-white border border-gray-200 rounded-2xl p-1 shadow-sm">
        <button
          v-for="t of tabs"
          :key="t.value"
          type="button"
          class="flex-1 md:flex-none md:px-6 flex items-center justify-center gap-1.5 h-10 rounded-xl text-sm font-medium transition-colors"
          :class="tab === t.value ? 'bg-primary-600 text-white' : 'text-gray-500 hover:bg-gray-50'"
          @click="tab = t.value"
        >
          <i :class="['pi', t.icon, 'text-xs']" />
          <span>{{ t.label }}</span>
          <span
            v-if="t.badge"
            class="min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center"
            :class="tab === t.value ? 'bg-white/25 text-white' : 'bg-red-100 text-red-600'"
          >
            {{ t.badge }}
          </span>
        </button>
      </div>
    </div>

    <div class="px-4 md:px-6">
      <!-- ============================ TODAY ============================ -->
      <div v-if="tab === 'today'" class="space-y-4 pt-2">
        <div class="grid grid-cols-2 md:grid-cols-4 gap-2">
          <button
            v-for="c of todayCounters"
            :key="c.label"
            type="button"
            class="bg-white border border-gray-200 rounded-2xl p-3 text-right shadow-sm active:bg-gray-50"
            @click="c.action()"
          >
            <div class="text-2xl font-bold" :class="c.color">{{ c.value }}</div>
            <div class="text-[11px] text-gray-500 mt-0.5">{{ c.label }}</div>
          </button>
        </div>

        <!-- Work through the backlog -->
        <div
          v-if="untouchedLeads.length"
          class="bg-gradient-to-l from-primary-600 to-indigo-600 rounded-2xl p-4 text-white shadow-sm md:flex md:items-center md:justify-between md:gap-4"
        >
          <div class="min-w-0">
            <div class="font-semibold">{{ untouchedLeads.length }} לידים שלא דיברת איתם מעולם</div>
            <div class="text-xs text-white/80 mt-1">
              עבור עליהם אחד-אחד: חייג, סכם, וסמן. הרשימה מתנקה תוך כדי.
            </div>
          </div>
          <Button
            label="התחל לעבור על הרשימה"
            icon="pi pi-play"
            class="!mt-3 md:!mt-0 !bg-white !text-primary-700 !border-0 !rounded-xl !font-semibold !h-10 flex-shrink-0"
            @click="startFocus(untouchedLeads)"
          />
        </div>

        <section v-if="overdueTasks.length">
          <h2 class="text-sm font-semibold text-red-600 mb-2 flex items-center gap-1.5">
            <i class="pi pi-exclamation-circle text-xs" /> באיחור ({{ overdueTasks.length }})
          </h2>
          <div class="space-y-2 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-2">
            <TaskRow
              v-for="t of overdueTasks"
              :key="t._id"
              :task="t"
              @toggle="(v) => toggleTask(t, v)"
              @open="openLeadFromTask(t)"
            />
          </div>
        </section>

        <section v-if="todayTasks.length">
          <h2 class="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-1.5">
            <i class="pi pi-calendar text-xs" /> להיום ({{ todayTasks.length }})
          </h2>
          <div class="space-y-2 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-2">
            <TaskRow
              v-for="t of todayTasks"
              :key="t._id"
              :task="t"
              show-time
              @toggle="(v) => toggleTask(t, v)"
              @open="openLeadFromTask(t)"
            />
          </div>
        </section>

        <section v-if="staleLeads.length">
          <div class="flex items-center justify-between mb-2">
            <h2 class="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
              <i class="pi pi-clock text-xs" /> נשכחו ({{ staleLeads.length }})
            </h2>
            <Button
              label="עבור עליהם"
              icon="pi pi-play"
              text
              size="small"
              class="!text-xs"
              @click="startFocus(staleLeads)"
            />
          </div>
          <div class="space-y-2 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-2">
            <LeadCard
              v-for="lead of staleLeads.slice(0, 5)"
              :key="lead._id"
              :lead="lead"
              @open="selectedLead = lead"
              @log="logLead = lead"
              @convert="convertLead(lead)"
              @filter-feature="filterByFeature"
            />
          </div>
        </section>

        <div
          v-if="!overdueTasks.length && !todayTasks.length && !untouchedLeads.length && !staleLeads.length"
          class="bg-white border border-gray-200 rounded-2xl p-10 text-center shadow-sm"
        >
          <i class="pi pi-check-circle text-3xl text-emerald-500" />
          <div class="text-sm text-gray-600 mt-3 font-medium">הכל נקי. אין מה לעשות עכשיו.</div>
        </div>
      </div>

      <!-- ============================ LEADS ============================ -->
      <div v-else-if="tab === 'leads'" class="pt-2">
        <!-- Search -->
        <IconField class="w-full mb-2">
          <InputIcon class="pi pi-search" />
          <InputText v-model="searchQuery" placeholder="חיפוש שם, טלפון או אימייל..." class="w-full !text-sm !h-11 !rounded-xl" />
        </IconField>

        <!-- Status chips: the primary filter -->
        <div class="flex gap-1.5 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0 md:flex-wrap">
          <button
            v-for="s of STAGE_OPTIONS"
            :key="s.value"
            type="button"
            class="flex items-center gap-1.5 px-3 h-9 rounded-full border text-xs font-medium whitespace-nowrap transition-colors flex-shrink-0"
            :class="
              activeStages.includes(s.value)
                ? STAGE_CHIP_CLASSES[s.value] + ' ring-2 ring-offset-1 ring-primary-400'
                : 'bg-white border-gray-200 text-gray-500'
            "
            @click="toggleStage(s.value)"
          >
            <i :class="['pi', STAGE_ICONS[s.value], 'text-[10px]']" />
            {{ s.label }}
            <span class="text-[10px] opacity-70">{{ stageCounts[s.value] || 0 }}</span>
          </button>
        </div>

        <!-- Secondary filters -->
        <div class="flex items-center justify-between gap-2 mb-2">
          <button
            type="button"
            class="flex items-center gap-1.5 text-xs text-gray-500 h-8"
            @click="showFilters = !showFilters"
          >
            <i :class="['pi', showFilters ? 'pi-chevron-up' : 'pi-sliders-h', 'text-[10px]']" />
            סינון נוסף
            <span v-if="extraFilterCount" class="bg-primary-100 text-primary-700 px-1.5 rounded-full text-[10px] font-semibold">
              {{ extraFilterCount }}
            </span>
          </button>
          <div class="flex items-center gap-2">
            <button type="button" class="text-xs text-gray-500 h-8 flex items-center gap-1.5" @click="toggleSelectionMode">
              <i :class="['pi', selectionMode ? 'pi-times' : 'pi-check-square', 'text-[10px]']" />
              {{ selectionMode ? 'בטל בחירה' : 'בחירה מרובה' }}
            </button>
            <Button
              v-if="filteredLeads.length"
              label="עבור עליהם"
              icon="pi pi-play"
              text
              size="small"
              class="!text-xs"
              @click="startFocus(filteredLeads)"
            />
          </div>
        </div>

        <div v-if="showFilters" class="bg-white border border-gray-200 rounded-2xl p-3 mb-3 shadow-sm space-y-2">
          <div class="grid grid-cols-2 lg:grid-cols-4 gap-2">
            <Select
              v-model="filterSource"
              :options="sourceFilterOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="כל המקורות"
              class="!text-xs"
              showClear
            />
            <Select
              v-model="filterPriority"
              :options="priorityFilterOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="כל העדיפויות"
              class="!text-xs"
              showClear
            />
            <Select
              v-model="filterAssignee"
              :options="assigneeFilterOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="כל האחראים"
              class="!text-xs"
              showClear
            />
            <Select
              v-model="filterFeature"
              :options="featureFilterOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="כל הפיצ'רים"
              class="!text-xs"
              showClear
            />
          </div>
          <div class="flex flex-wrap gap-x-4 gap-y-2 pt-1">
            <label class="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
              <ToggleSwitch v-model="hideTestBusinesses" /> הסתר עסקי בדיקה
            </label>
            <label
              class="flex items-center gap-2 text-xs text-gray-600 cursor-pointer"
              title="עסקים בתוכנית פרימיום עם חוזה פעיל — כבר לקוחות משלמים"
            >
              <ToggleSwitch v-model="hideCustomers" /> הסתר לקוחות משלמים
            </label>
            <label class="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
              <ToggleSwitch v-model="onlyNeverContacted" /> לא דובר מעולם
            </label>
            <label class="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
              <ToggleSwitch v-model="onlyWithOpenTasks" /> עם משימה פתוחה
            </label>
          </div>
        </div>

        <!-- Loading -->
        <div v-if="loading && !leads.length" class="flex justify-center items-center h-64">
          <ProgressSpinner style="width: 44px; height: 44px" />
        </div>

        <!-- List -->
        <template v-else>
          <div class="flex items-center justify-between text-xs text-gray-400 mb-2">
            <span>
              {{ filteredLeads.length }} לידים
              <template v-if="isSearching"> · חיפוש בכל הסטטוסים</template>
            </span>
            <span v-if="selectionMode && selectedIds.size">{{ selectedIds.size }} נבחרו</span>
          </div>

          <div class="space-y-2 lg:space-y-0 lg:grid lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 lg:gap-2">
            <LeadCard
              v-for="lead of visibleLeads"
              :key="lead._id"
              :lead="lead"
              :selectable="selectionMode"
              :selected="selectedIds.has(lead._id as string)"
              @open="selectedLead = lead"
              @log="logLead = lead"
              @convert="convertLead(lead)"
              @toggle-select="toggleSelected(lead)"
              @filter-feature="filterByFeature"
            />
          </div>

          <div v-if="!filteredLeads.length" class="bg-white border border-gray-200 rounded-2xl p-10 text-center shadow-sm">
            <i class="pi pi-inbox text-3xl text-gray-300" />
            <div class="text-sm text-gray-500 mt-3">אין לידים שמתאימים לסינון</div>
          </div>

          <Button
            v-if="visibleLeads.length < filteredLeads.length"
            :label="`טען עוד (${filteredLeads.length - visibleLeads.length})`"
            outlined
            class="w-full !mt-3 !rounded-xl !h-11"
            @click="visibleCount += PAGE_STEP"
          />
        </template>
      </div>

      <!-- ============================ CALENDAR ============================ -->
      <div v-else class="pt-2">
        <div class="flex items-center justify-between gap-2 flex-wrap mb-2">
          <Button
            icon="pi pi-bolt"
            label="הרץ אוטומציות"
            text
            size="small"
            class="!text-xs"
            :loading="runningAutomations"
            title="יצירת תזכורות אוטומטיות לניסיונות שמסתיימים בקרוב"
            @click="runAutomations"
          />
          <div class="flex items-center gap-3 flex-wrap">
            <!-- Assignee tabs: whose tasks am I looking at -->
            <div class="flex gap-1 bg-white border border-gray-200 rounded-xl p-0.5">
              <button
                v-for="o of calendarAssigneeOptions"
                :key="o.value ?? 'all'"
                type="button"
                class="px-3 h-7 rounded-lg text-xs font-medium transition-colors"
                :class="
                  calendarAssignee === o.value
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-500 hover:bg-gray-50'
                "
                @click="calendarAssignee = o.value"
              >
                {{ o.label }}
              </button>
            </div>
            <label class="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
              <ToggleSwitch v-model="showCompletedTasks" /> הצג שהושלמו
            </label>
          </div>
        </div>
        <TasksCalendar
          :tasks="calendarTasks"
          @toggle="(task, completed) => toggleTask(task, completed)"
          @open-lead="openLeadFromTask"
        />
      </div>
    </div>

    <!-- Bulk action bar -->
    <div
      v-if="selectionMode && selectedIds.size"
      class="fixed bottom-0 inset-x-0 z-30 bg-white border-t border-gray-200 shadow-lg px-4 md:px-6 py-2.5"
      :style="{ paddingBottom: 'calc(0.625rem + env(safe-area-inset-bottom))' }"
    >
      <div class="flex items-center gap-2 overflow-x-auto">
        <span class="text-xs font-medium text-gray-600 whitespace-nowrap">{{ selectedIds.size }} נבחרו</span>
        <Button
          v-for="a of bulkActions"
          :key="a.label"
          :label="a.label"
          size="small"
          outlined
          class="!rounded-xl !text-xs whitespace-nowrap"
          :loading="bulkSaving"
          @click="a.run()"
        />
      </div>
    </div>

    <!-- Focus mode banner -->
    <div
      v-if="focusQueue.length"
      class="fixed bottom-0 inset-x-0 z-30 bg-primary-600 text-white px-4 py-3 shadow-lg"
      :style="{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }"
    >
      <div class="flex items-center justify-between gap-3">
        <div class="min-w-0">
          <div class="text-xs text-white/80">מעבר על הרשימה</div>
          <div class="font-semibold text-sm truncate">
            {{ focusIndex + 1 }} מתוך {{ focusQueue.length }} · {{ focusQueue[focusIndex]?.businessName }}
          </div>
        </div>
        <div class="flex items-center gap-1.5 flex-shrink-0">
          <Button label="המשך" size="small" class="!bg-white !text-primary-700 !border-0 !rounded-xl" @click="logLead = focusQueue[focusIndex]" />
          <Button icon="pi pi-times" text rounded size="small" class="!text-white" @click="stopFocus" />
        </div>
      </div>
    </div>

    <!-- New lead -->
    <button
      v-if="!focusQueue.length && !(selectionMode && selectedIds.size)"
      type="button"
      class="md:hidden fixed bottom-5 left-5 z-30 w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-600 to-indigo-600 text-white shadow-lg flex items-center justify-center active:scale-95 transition-transform"
      :style="{ bottom: 'calc(1.25rem + env(safe-area-inset-bottom))' }"
      @click="showNewLeadDialog = true"
    >
      <i class="pi pi-plus text-lg" />
    </button>

    <LeadDetailDrawer
      :lead="selectedLead"
      :popular-features="popularFeatures"
      @update="onLeadUpdate"
      @delete="onLeadDelete"
      @log="(lead) => (logLead = lead)"
      @tasks-changed="fetchTasks"
      @close="selectedLead = null"
    />

    <QuickLogSheet
      :lead="logLead"
      :popular-features="popularFeatures"
      :next-label="focusQueue.length ? 'שמור והבא' : undefined"
      @saved="onLogged"
      @skip="advanceFocus"
      @close="logLead = null"
    />

    <NewLeadDialog v-model="showNewLeadDialog" @created="onLeadCreated" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import type {
  IPlatformFeaturePopularity,
  IPlatformLead,
  IPlatformLeadWithStats,
  IPlatformReminder,
} from '@torim-app/types';
import {
  ContractStatus,
  DEFAULT_VISIBLE_STAGES,
  Plans,
  PlatformLeadPriorityEnum,
  PlatformLeadSourceEnum,
  PlatformLeadStageEnum,
} from '@torim-app/types';
import { CrmApi } from '../api/crm-api';
import { RemindersApi } from '../api/reminders-api';
import LeadCard from '../components/leads/LeadCard.vue';
import LeadDetailDrawer from '../components/leads/LeadDetailDrawer.vue';
import QuickLogSheet from '../components/leads/QuickLogSheet.vue';
import TaskRow from '../components/leads/TaskRow.vue';
import TasksCalendar from '../components/leads/TasksCalendar.vue';
import NewLeadDialog from '../components/NewLeadDialog.vue';
import { useNotify } from '../composables/useNotify';
import {
  ASSIGNEES,
  PRIORITY_LABELS,
  SOURCE_LABELS,
  STAGE_CHIP_CLASSES,
  STAGE_ICONS,
  STAGE_OPTIONS,
  assigneeLabel,
  isLeadStale,
  isTaskOverdue,
  isTaskToday,
} from '../composables/useLeadDisplay';

type TabValue = 'today' | 'leads' | 'calendar';

const PAGE_STEP = 50;
/** Statuses the operator last chose to see, so the filter survives a reload */
const STAGES_STORAGE_KEY = 'platform-crm-stages';

const { notifySuccess, notifyError } = useNotify();
const route = useRoute();
const router = useRouter();

const loading = ref(false);
const syncing = ref(false);
const bulkSaving = ref(false);
const runningAutomations = ref(false);

const leads = ref<IPlatformLeadWithStats[]>([]);
const tasks = ref<IPlatformReminder[]>([]);
const popularFeatures = ref<IPlatformFeaturePopularity[]>([]);

const tab = ref<TabValue>('today');
const selectedLead = ref<IPlatformLeadWithStats | null>(null);
const logLead = ref<IPlatformLeadWithStats | null>(null);
const showNewLeadDialog = ref(false);
const showFilters = ref(false);
const showCompletedTasks = ref(false);

const searchQuery = ref('');
const activeStages = ref<PlatformLeadStageEnum[]>(loadStages());
const filterSource = ref<PlatformLeadSourceEnum | null>(null);
const filterPriority = ref<PlatformLeadPriorityEnum | null>(null);
const filterAssignee = ref<string | null>(null);
const filterFeature = ref<string | null>(null);
const hideTestBusinesses = ref(true);
const hideCustomers = ref(true);
const onlyNeverContacted = ref(false);
const onlyWithOpenTasks = ref(false);

const selectionMode = ref(false);
const selectedIds = ref(new Set<string>());
const visibleCount = ref(PAGE_STEP);

const focusQueue = ref<IPlatformLeadWithStats[]>([]);
const focusIndex = ref(0);

function loadStages(): PlatformLeadStageEnum[] {
  try {
    const raw = localStorage.getItem(STAGES_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as PlatformLeadStageEnum[];
      if (Array.isArray(parsed) && parsed.length) return parsed;
    }
  } catch {
    // A corrupt value is not worth failing the screen over
  }
  return [...DEFAULT_VISIBLE_STAGES];
}

watch(activeStages, (val) => {
  try {
    localStorage.setItem(STAGES_STORAGE_KEY, JSON.stringify(val));
  } catch {
    // Storage can be unavailable; the filter still works for this session
  }
}, { deep: true });

// --- Derived data -----------------------------------------------------------

const openTasks = computed(() => tasks.value.filter((t) => !t.completed));
const overdueTasks = computed(() => openTasks.value.filter(isTaskOverdue));
const todayTasks = computed(() => openTasks.value.filter((t) => !isTaskOverdue(t) && isTaskToday(t)));

/** null = everyone; '' = unassigned tasks (the automations create those) */
const calendarAssignee = ref<string | null>(null);

const calendarAssigneeOptions: Array<{ value: string | null; label: string }> = [
  { value: null, label: 'הכל' },
  ...ASSIGNEES,
  { value: '', label: 'לא משויך' },
];

const calendarTasks = computed(() => {
  const list = showCompletedTasks.value ? tasks.value : openTasks.value;
  if (calendarAssignee.value === null) return list;
  if (calendarAssignee.value === '') return list.filter((t) => !t.assignedTo);
  return list.filter((t) => t.assignedTo === calendarAssignee.value);
});

/**
 * Already a paying customer — premium plan on an active contract, the same
 * definition the MRR / "עסקים משלמים" numbers use. Nothing left to sell them.
 */
const isPayingCustomer = (lead: IPlatformLeadWithStats): boolean =>
  lead.business?.plan === Plans.PREMIUM && lead.business?.contractStatus === ContractStatus.Active;

/** Everything the operator is allowed to see, before the pipeline filters */
const visibleLeadsBase = computed(() =>
  hideTestBusinesses.value ? leads.value.filter((l) => !l.isTestBusiness) : leads.value,
);

const activeLeads = computed(() =>
  hideCustomers.value
    ? visibleLeadsBase.value.filter((l) => !isPayingCustomer(l))
    : visibleLeadsBase.value,
);

const untouchedLeads = computed(() =>
  activeLeads.value.filter(
    (l) => !l.lastContactedAt && l.stage === PlatformLeadStageEnum.New,
  ),
);

const staleLeads = computed(() =>
  activeLeads.value
    .filter((l) => isLeadStale(l) && !!l.lastContactedAt)
    .sort(
      (a, b) =>
        new Date(a.lastContactedAt as string).getTime() -
        new Date(b.lastContactedAt as string).getTime(),
    ),
);

const stageCounts = computed(() => {
  const counts: Partial<Record<PlatformLeadStageEnum, number>> = {};
  for (const lead of activeLeads.value) {
    counts[lead.stage] = (counts[lead.stage] || 0) + 1;
  }
  return counts;
});

const isSearching = computed(() => !!searchQuery.value.trim());

const filteredLeads = computed(() => {
  // A search has to reach every lead — including paying customers and statuses
  // outside the chip selection, which is remembered across sessions and would
  // otherwise hide a match silently.
  let list = isSearching.value ? visibleLeadsBase.value : activeLeads.value;

  if (!isSearching.value && activeStages.value.length) {
    list = list.filter((l) => activeStages.value.includes(l.stage));
  }
  if (filterSource.value) list = list.filter((l) => l.source === filterSource.value);
  if (filterPriority.value) list = list.filter((l) => l.priority === filterPriority.value);
  if (filterAssignee.value) list = list.filter((l) => l.assignedTo === filterAssignee.value);
  if (onlyNeverContacted.value) list = list.filter((l) => !l.lastContactedAt);
  if (onlyWithOpenTasks.value) list = list.filter((l) => !!l.openTasksCount);

  if (filterFeature.value) {
    const q = filterFeature.value.toLowerCase();
    list = list.filter((l) => l.requestedFeatures?.some((f) => f.toLowerCase().includes(q)));
  }

  if (searchQuery.value.trim()) {
    const q = searchQuery.value.trim().toLowerCase();
    list = list.filter(
      (l) =>
        l.businessName?.toLowerCase().includes(q) ||
        l.contactName?.toLowerCase().includes(q) ||
        l.phone?.includes(q) ||
        l.email?.toLowerCase().includes(q) ||
        l.bid?.toLowerCase().includes(q),
    );
  }

  return list;
});

const visibleLeads = computed(() => filteredLeads.value.slice(0, visibleCount.value));

// Reset paging whenever the result set changes underneath the operator
watch(
  [filteredLeads],
  () => {
    visibleCount.value = PAGE_STEP;
  },
);

const tabs = computed(() => [
  {
    value: 'today' as TabValue,
    label: 'היום',
    icon: 'pi-bolt',
    badge: overdueTasks.value.length + todayTasks.value.length || 0,
  },
  { value: 'leads' as TabValue, label: 'לידים', icon: 'pi-users', badge: 0 },
  { value: 'calendar' as TabValue, label: 'יומן', icon: 'pi-calendar', badge: 0 },
]);

const todayCounters = computed(() => [
  {
    label: 'באיחור',
    value: overdueTasks.value.length,
    color: overdueTasks.value.length ? 'text-red-600' : 'text-gray-900',
    action: () => (tab.value = 'today'),
  },
  {
    label: 'בטיפול',
    value: stageCounts.value[PlatformLeadStageEnum.InProgress] || 0,
    color: 'text-purple-600',
    action: () => showOnly(PlatformLeadStageEnum.InProgress),
  },
  {
    label: 'חדשים',
    value: stageCounts.value[PlatformLeadStageEnum.New] || 0,
    color: 'text-blue-600',
    action: () => showOnly(PlatformLeadStageEnum.New),
  },
  {
    label: 'בהמתנה',
    value: stageCounts.value[PlatformLeadStageEnum.OnHold] || 0,
    color: 'text-amber-600',
    action: () => showOnly(PlatformLeadStageEnum.OnHold),
  },
]);


const sourceFilterOptions = Object.values(PlatformLeadSourceEnum).map((v) => ({
  label: SOURCE_LABELS[v],
  value: v,
}));

const priorityFilterOptions = Object.values(PlatformLeadPriorityEnum).map((v) => ({
  label: PRIORITY_LABELS[v],
  value: v,
}));

const assigneeFilterOptions = ASSIGNEES;

const featureFilterOptions = computed(() =>
  popularFeatures.value.map((f) => ({ label: `${f.feature} (${f.count})`, value: f.feature })),
);

const extraFilterCount = computed(
  () =>
    [
      filterSource.value,
      filterPriority.value,
      filterAssignee.value,
      filterFeature.value,
      onlyNeverContacted.value || null,
      onlyWithOpenTasks.value || null,
    ].filter(Boolean).length,
);

const bulkActions = computed(() => [
  { label: 'הומר ללקוח', run: () => bulkSetStage(PlatformLeadStageEnum.Converted) },
  { label: 'סמן כקר', run: () => bulkSetStage(PlatformLeadStageEnum.Cold) },
  { label: 'סמן כספאם', run: () => bulkSetStage(PlatformLeadStageEnum.Spam) },
  { label: 'סמן כטופל', run: () => bulkSetStage(PlatformLeadStageEnum.Done) },
  ...ASSIGNEES.map((a) => ({ label: `שייך ל${a.label}`, run: () => bulkAssign(a.value) })),
]);

// --- Actions ----------------------------------------------------------------

const showOnly = (stage: PlatformLeadStageEnum) => {
  activeStages.value = [stage];
  tab.value = 'leads';
};

const toggleStage = (stage: PlatformLeadStageEnum) => {
  const idx = activeStages.value.indexOf(stage);
  if (idx >= 0) activeStages.value = activeStages.value.filter((s) => s !== stage);
  else activeStages.value = [...activeStages.value, stage];
};

const filterByFeature = (feature: string) => {
  filterFeature.value = feature;
  showFilters.value = true;
  tab.value = 'leads';
};

const toggleSelectionMode = () => {
  selectionMode.value = !selectionMode.value;
  if (!selectionMode.value) selectedIds.value = new Set();
};

const toggleSelected = (lead: IPlatformLeadWithStats) => {
  if (!lead._id) return;
  const next = new Set(selectedIds.value);
  if (next.has(lead._id)) next.delete(lead._id);
  else next.add(lead._id);
  selectedIds.value = next;
};

/**
 * Mark a lead as a won customer. Converted is not in the default visible stages,
 * so the card drops out of the list as soon as the stage lands.
 */
const convertLead = async (lead: IPlatformLeadWithStats) => {
  if (!lead._id) return;
  const previous = lead.stage;
  lead.stage = PlatformLeadStageEnum.Converted;
  try {
    await CrmApi.updateLead(lead._id, { stage: PlatformLeadStageEnum.Converted });
    notifySuccess(`${lead.businessName} סומן כלקוח`);
  } catch (e) {
    lead.stage = previous;
    console.error('Failed to convert lead:', e);
    notifyError('סימון הליד נכשל');
  }
};

const bulkSetStage = async (stage: PlatformLeadStageEnum) => {
  const ids = [...selectedIds.value];
  if (!ids.length) return;
  bulkSaving.value = true;
  try {
    await CrmApi.bulkUpdate({ ids, stage });
    for (const lead of leads.value) {
      if (lead._id && selectedIds.value.has(lead._id)) lead.stage = stage;
    }
    selectedIds.value = new Set();
    selectionMode.value = false;
    notifySuccess(`${ids.length} לידים עודכנו`);
  } catch (e) {
    console.error('Bulk update failed:', e);
    notifyError('עדכון מרובה נכשל');
  } finally {
    bulkSaving.value = false;
  }
};

const bulkAssign = async (assignee: string) => {
  const ids = [...selectedIds.value];
  if (!ids.length) return;
  bulkSaving.value = true;
  try {
    await CrmApi.bulkUpdate({ ids, assignedTo: assignee });
    for (const lead of leads.value) {
      if (lead._id && selectedIds.value.has(lead._id)) lead.assignedTo = assignee;
    }
    selectedIds.value = new Set();
    selectionMode.value = false;
    notifySuccess(`${ids.length} לידים שויכו ל${assigneeLabel(assignee)}`);
  } catch (e) {
    console.error('Bulk assign failed:', e);
    notifyError('שיוך מרובה נכשל');
  } finally {
    bulkSaving.value = false;
  }
};

const startFocus = (queue: IPlatformLeadWithStats[]) => {
  if (!queue.length) return;
  focusQueue.value = [...queue];
  focusIndex.value = 0;
  logLead.value = focusQueue.value[0];
};

const stopFocus = () => {
  focusQueue.value = [];
  focusIndex.value = 0;
  logLead.value = null;
};

const advanceFocus = () => {
  if (!focusQueue.value.length) {
    logLead.value = null;
    return;
  }
  if (focusIndex.value + 1 >= focusQueue.value.length) {
    notifySuccess('סיימת את הרשימה', 'כל הלידים בתור טופלו');
    stopFocus();
    return;
  }
  focusIndex.value++;
  logLead.value = focusQueue.value[focusIndex.value];
};

const replaceLead = (updated: IPlatformLead | IPlatformLeadWithStats) => {
  const idx = leads.value.findIndex((l) => l._id === updated._id);
  if (idx >= 0) leads.value[idx] = { ...leads.value[idx], ...updated };
  if (selectedLead.value?._id === updated._id) {
    selectedLead.value = { ...selectedLead.value, ...updated };
  }
};

const onLeadUpdate = (updated: IPlatformLead) => {
  replaceLead(updated);
  fetchPopularFeatures();
};

const onLogged = async (updated: IPlatformLeadWithStats) => {
  replaceLead(updated);
  logLead.value = null;
  await Promise.all([fetchTasks(), fetchPopularFeatures()]);
  if (focusQueue.value.length) advanceFocus();
};

const onLeadDelete = (deleted: IPlatformLead) => {
  leads.value = leads.value.filter((l) => l._id !== deleted._id);
  selectedLead.value = null;
};

const onLeadCreated = (lead: IPlatformLead) => {
  leads.value.unshift(lead as IPlatformLeadWithStats);
  tab.value = 'leads';
};

const openLeadFromTask = (task: IPlatformReminder) => {
  if (!task.leadId) return;
  const lead = leads.value.find((l) => l._id === task.leadId);
  if (lead) selectedLead.value = lead;
};

const toggleTask = async (task: IPlatformReminder, completed: boolean) => {
  if (!task._id) return;
  try {
    await RemindersApi.update(task._id, { completed });
    const idx = tasks.value.findIndex((t) => t._id === task._id);
    if (idx >= 0) tasks.value[idx] = { ...tasks.value[idx], completed };
    // The lead card shows the nearest open task, so it has to follow along
    await fetchLeads();
  } catch (e) {
    console.error('Failed to toggle task:', e);
    notifyError('עדכון המשימה נכשל');
  }
};

// --- Fetching ---------------------------------------------------------------

const fetchLeads = async () => {
  try {
    leads.value = await CrmApi.fetchAllLeads({ withStats: true });
  } catch (e) {
    console.error('Failed to fetch leads:', e);
    notifyError('טעינת הלידים נכשלה');
  }
};

const fetchTasks = async () => {
  try {
    tasks.value = await RemindersApi.list();
  } catch (e) {
    console.error('Failed to fetch tasks:', e);
  }
};

const fetchPopularFeatures = async () => {
  try {
    popularFeatures.value = await CrmApi.fetchPopularFeatures();
  } catch (e) {
    console.error('Failed to fetch popular features:', e);
  }
};

const refresh = async () => {
  loading.value = true;
  try {
    await Promise.all([fetchLeads(), fetchTasks(), fetchPopularFeatures()]);
  } finally {
    loading.value = false;
  }
};

const runAutomations = async () => {
  runningAutomations.value = true;
  try {
    const res = await RemindersApi.runAutomations();
    const created = res.trialReminders;
    notifySuccess(
      'האוטומציות רצו',
      created ? `נוצרו ${created} תזכורות` : 'אין תזכורות חדשות',
    );
    await fetchTasks();
  } catch (e) {
    console.error('Failed to run automations:', e);
    notifyError('הרצת האוטומציות נכשלה');
  } finally {
    runningAutomations.value = false;
  }
};

const syncLeads = async () => {
  syncing.value = true;
  try {
    const result = await CrmApi.syncLeads();
    notifySuccess(
      'סנכרון הושלם',
      result.created > 0
        ? `נוספו ${result.created} לידים מתוך ${result.total} עסקים רשומים`
        : 'אין לידים חדשים לייבוא',
    );
    await refresh();
  } catch (e) {
    console.error('Failed to sync leads:', e);
    notifyError('סנכרון הלידים נכשל');
  } finally {
    syncing.value = false;
  }
};

// --- Deep links -------------------------------------------------------------

watch(
  () => route.query,
  async (q) => {
    if (q.tab && typeof q.tab === 'string' && ['today', 'leads', 'calendar'].includes(q.tab)) {
      tab.value = q.tab as TabValue;
    }
    if (q.bid && typeof q.bid === 'string') {
      if (!leads.value.length) await fetchLeads();
      const match = leads.value.find((l) => l.bid === q.bid);
      if (match) {
        selectedLead.value = match;
      } else {
        searchQuery.value = q.bid;
        tab.value = 'leads';
        notifyError('לא נמצא ליד לעסק זה', 'אפשר ליצור ליד חדש או להריץ סנכרון');
      }
    }
    if (q.tab || q.bid) router.replace({ query: {} });
  },
  { immediate: true },
);

onMounted(() => {
  refresh();
});
</script>
